import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Wizard Transition Component
 * Provides smooth transitions between wizard steps with animations
 */
const WizardTransition = ({
  children,
  currentStep,
  direction = 'forward', // 'forward', 'backward'
  duration = 300,
  className = '',
  onTransitionComplete = null,
  disabled = false
}) => {
  const { theme } = useTheme();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayStep, setDisplayStep] = useState(currentStep);
  const containerRef = useRef(null);
  const timeoutRef = useRef(null);

  /**
   * Handle step transition
   */
  useEffect(() => {
    if (disabled || currentStep === displayStep) return;

    setIsTransitioning(true);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Start transition
    timeoutRef.current = setTimeout(() => {
      setDisplayStep(currentStep);
      
      // Complete transition after animation
      setTimeout(() => {
        setIsTransitioning(false);
        if (onTransitionComplete) {
          onTransitionComplete(currentStep);
        }
      }, duration);
    }, 50); // Small delay to ensure smooth transition

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [currentStep, displayStep, duration, onTransitionComplete, disabled]);

  /**
   * Get transition classes
   */
  const getTransitionClasses = () => {
    if (disabled) return '';

    const baseClasses = 'transition-all ease-in-out';
    const durationClass = `duration-${duration}`;
    
    if (isTransitioning) {
      const slideDirection = direction === 'forward' ? 'translate-x-full' : '-translate-x-full';
      return `${baseClasses} ${durationClass} ${slideDirection} opacity-0`;
    }
    
    return `${baseClasses} ${durationClass} translate-x-0 opacity-100`;
  };

  /**
   * Get container classes
   */
  const getContainerClasses = () => {
    const baseClasses = 'relative overflow-hidden';
    
    if (isTransitioning) {
      return `${baseClasses} pointer-events-none`;
    }
    
    return baseClasses;
  };

  return (
    <div 
      ref={containerRef}
      className={`wizard-transition ${getContainerClasses()} ${className}`}
      style={{ minHeight: '400px' }}
    >
      <div className={getTransitionClasses()}>
        {React.cloneElement(children, { 
          key: displayStep,
          isTransitioning,
          transitionDirection: direction
        })}
      </div>
      
      {/* Loading overlay during transition */}
      {isTransitioning && (
        <div className={`absolute inset-0 flex items-center justify-center ${
          theme === 'dark' 
            ? 'bg-gray-900 bg-opacity-50' 
            : 'bg-white bg-opacity-50'
        } backdrop-blur-sm z-10`}>
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
            <span className={`text-sm ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {direction === 'forward' ? 'Moving to next step...' : 'Going back...'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Fade Transition Component
 * Simple fade in/out transition
 */
export const FadeTransition = ({
  children,
  show = true,
  duration = 200,
  className = '',
  onTransitionComplete = null
}) => {
  const [isVisible, setIsVisible] = useState(show);
  const [shouldRender, setShouldRender] = useState(show);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (show) {
      setShouldRender(true);
      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, 10);
    } else {
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        if (onTransitionComplete) {
          onTransitionComplete();
        }
      }, duration);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [show, duration, onTransitionComplete]);

  if (!shouldRender) return null;

  return (
    <div 
      className={`transition-opacity duration-${duration} ease-in-out ${
        isVisible ? 'opacity-100' : 'opacity-0'
      } ${className}`}
    >
      {children}
    </div>
  );
};

/**
 * Slide Transition Component
 * Slide in/out transition with direction control
 */
export const SlideTransition = ({
  children,
  show = true,
  direction = 'right', // 'left', 'right', 'up', 'down'
  duration = 300,
  className = '',
  onTransitionComplete = null
}) => {
  const [isVisible, setIsVisible] = useState(show);
  const [shouldRender, setShouldRender] = useState(show);
  const timeoutRef = useRef(null);

  const getTransformClasses = () => {
    const directionMap = {
      left: isVisible ? 'translate-x-0' : '-translate-x-full',
      right: isVisible ? 'translate-x-0' : 'translate-x-full',
      up: isVisible ? 'translate-y-0' : '-translate-y-full',
      down: isVisible ? 'translate-y-0' : 'translate-y-full'
    };
    
    return directionMap[direction] || directionMap.right;
  };

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (show) {
      setShouldRender(true);
      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, 10);
    } else {
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        if (onTransitionComplete) {
          onTransitionComplete();
        }
      }, duration);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [show, duration, onTransitionComplete]);

  if (!shouldRender) return null;

  return (
    <div 
      className={`transform transition-transform duration-${duration} ease-in-out ${
        getTransformClasses()
      } ${className}`}
    >
      {children}
    </div>
  );
};

/**
 * Scale Transition Component
 * Scale in/out transition
 */
export const ScaleTransition = ({
  children,
  show = true,
  duration = 200,
  className = '',
  onTransitionComplete = null
}) => {
  const [isVisible, setIsVisible] = useState(show);
  const [shouldRender, setShouldRender] = useState(show);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (show) {
      setShouldRender(true);
      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, 10);
    } else {
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        if (onTransitionComplete) {
          onTransitionComplete();
        }
      }, duration);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [show, duration, onTransitionComplete]);

  if (!shouldRender) return null;

  return (
    <div 
      className={`transform transition-all duration-${duration} ease-in-out ${
        isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      } ${className}`}
    >
      {children}
    </div>
  );
};

export default WizardTransition;
